import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { showToast } from "@/app/_module/utils/show-toast";
import { transformErrorResponse } from "@/app/_module/utils/transform-error-response";
import type { RoutePlan, NearbyLead, MileageSummary, BestKnockTimes, RouteAnalytics } from "../types/types";

export const routesService = {
  optimizeRoute: async (data: {
    leadIds: string[];
    name?: string;
    startLat?: number;
    startLng?: number;
    trafficAware?: boolean;
    // Coordinates geocoded IN THE BROWSER (from the user's own IP). Sent so the
    // server never calls a public geocoder (no shared server-IP rate-limit/ban).
    geocodes?: Array<{ leadId: string; lat: number; lng: number }>;
  }) => {
    try {
      return await handleApiCall<{ success: boolean; route: RoutePlan }>(
        async () => {
          const response = await api.post<{
            success: boolean;
            route: RoutePlan;
          }>({
            url: getApiUrl(endpoint.routes.optimize),
            body: data,
            auth: true,
            // Route optimize geocodes EVERY selected lead (Nominatim paced ~1
            // req/sec), so a large door-knock batch can legitimately take 10-20+
            // minutes. Override the global 60s axios timeout so the request WAITS
            // instead of aborting at 60s (the "unknown error" 499s). 30 min covers
            // the 1,000-lead-per-route max with margin.
            options: { timeout: 1_800_000 },
          });
          return response;
        },
      );
    } catch (error) {
      // Never surface "An unknown error occurred" / raw axios timeout text for
      // optimize — map every failure shape to something the agent can act on.
      const t = transformErrorResponse(error);
      const raw = `${(error as any)?.message || ""} ${(error as any)?.code || ""} ${t?.message || ""}`.toLowerCase();
      let friendly = t?.message || "";
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        friendly = "You're offline — check your signal and try again. Your selected leads are still here.";
      } else if (raw.includes("timeout") || raw.includes("econnaborted") || raw.includes("network error") || raw.includes("499")) {
        friendly = "The route took too long to build. Try again — addresses already located are saved, so the next run is faster. If it keeps happening, select fewer stops.";
      } else if (!friendly || raw.includes("unknown error")) {
        friendly = "Couldn't build the route — please try again. If it keeps happening, select fewer stops.";
      }
      showToast(friendly, "error");
      throw transformErrorResponse(error);
    }
  },

  getRoutes: async () => {
    try {
      return await handleApiCall<{ success: boolean; routes: RoutePlan[] }>(
        async () => {
          const response = await api.get<{
            success: boolean;
            routes: RoutePlan[];
          }>({
            url: getApiUrl(endpoint.routes.list),
            auth: true,
          });
          return response;
        },
      );
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      throw transformErrorResponse(error);
    }
  },

  getRouteById: async (routeId: string) => {
    try {
      return await handleApiCall<{ success: boolean; route: RoutePlan }>(
        async () => {
          const response = await api.get<{
            success: boolean;
            route: RoutePlan;
          }>({
            url: getApiUrl(endpoint.routes.getById(routeId)),
            auth: true,
          });
          return response;
        },
      );
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      throw transformErrorResponse(error);
    }
  },

  updateRoute: async (
    routeId: string,
    data: {
      status?: string;
      leadId?: string;
      visited?: boolean;
      skipped?: boolean;
      // Door disposition recorded on the knock: 'refused' (answered, no appt),
      // 'no_sale' (presented, didn't buy), 'sold' (presented + sold).
      outcome?: 'refused' | 'no_sale' | 'sold';
    },
  ) => {
    try {
      return await handleApiCall<{ success: boolean; route: RoutePlan }>(
        async () => {
          const response = await api.patch<{
            success: boolean;
            route: RoutePlan;
          }>({
            url: getApiUrl(endpoint.routes.update(routeId)),
            body: data,
            auth: true,
          });
          return response;
        },
      );
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      throw transformErrorResponse(error);
    }
  },

  deleteRoute: async (routeId: string) => {
    try {
      return await handleApiCall<{ success: boolean }>(async () => {
        const response = await api.delete<{ success: boolean }>({
          url: getApiUrl(endpoint.routes.delete(routeId)),
          auth: true,
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      throw transformErrorResponse(error);
    }
  },

  createFollowUp: async (routeId: string, data?: { name?: string; startLat?: number; startLng?: number }) => {
    try {
      return await handleApiCall<{ success: boolean; route: RoutePlan }>(async () =>
        api.post<{ success: boolean; route: RoutePlan }>({
          url: getApiUrl(endpoint.routes.followUp(routeId)),
          body: data || {},
          auth: true,
        }),
      );
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      throw transformErrorResponse(error);
    }
  },

  reOptimize: async (routeId: string, currentLat?: number, currentLng?: number) => {
    try {
      return await handleApiCall<{ success: boolean; route: RoutePlan }>(async () =>
        api.post<{ success: boolean; route: RoutePlan }>({
          url: getApiUrl(endpoint.routes.reOptimize(routeId)),
          body: { currentLat, currentLng },
          auth: true,
        }),
      );
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      throw transformErrorResponse(error);
    }
  },

  checkIn: async (routeId: string, data: { leadId: string; notes?: string; outcome?: string; gpsLat?: number; gpsLng?: number }) => {
    try {
      return await handleApiCall<{ success: boolean; route: RoutePlan }>(async () =>
        api.post<{ success: boolean; route: RoutePlan }>({
          url: getApiUrl(endpoint.routes.checkIn(routeId)),
          body: data,
          auth: true,
        }),
      );
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      throw transformErrorResponse(error);
    }
  },

  getNearbyLeads: async (lat: number, lng: number, radius?: number, excludeIds?: string[]) => {
    try {
      const params = new URLSearchParams({ lat: String(lat), lng: String(lng) });
      if (radius) params.set("radius", String(radius));
      if (excludeIds?.length) params.set("excludeIds", excludeIds.join(","));
      return await handleApiCall<{ success: boolean; leads: NearbyLead[] }>(async () =>
        api.get<{ success: boolean; leads: NearbyLead[] }>({
          url: getApiUrl(`${endpoint.routes.nearbyLeads}?${params.toString()}`),
          auth: true,
        }),
      );
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      throw transformErrorResponse(error);
    }
  },

  getMileage: async (startDate?: string, endDate?: string) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      return await handleApiCall<{ success: boolean } & MileageSummary>(async () =>
        api.get<{ success: boolean } & MileageSummary>({
          url: getApiUrl(`${endpoint.routes.mileage}?${params.toString()}`),
          auth: true,
        }),
      );
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      throw transformErrorResponse(error);
    }
  },

  getBestTimes: async (state?: string) => {
    try {
      const params = state ? `?state=${state}` : "";
      return await handleApiCall<{ success: boolean } & BestKnockTimes>(async () =>
        api.get<{ success: boolean } & BestKnockTimes>({
          url: getApiUrl(`${endpoint.routes.bestTimes}${params}`),
          auth: true,
        }),
      );
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      throw transformErrorResponse(error);
    }
  },

  getAnalytics: async (startDate?: string, endDate?: string) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      return await handleApiCall<{ success: boolean } & RouteAnalytics>(async () =>
        api.get<{ success: boolean } & RouteAnalytics>({
          url: getApiUrl(`${endpoint.routes.analytics}?${params.toString()}`),
          auth: true,
        }),
      );
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      throw transformErrorResponse(error);
    }
  },
};
